import { useAppDispatch } from '../../../../redux/hooks'
import React, { FC, useContext, useState, useEffect, useCallback, useRef, memo } from 'react'
import { ScreenContext } from 'react-components'
import { useSelector } from 'react-redux'
import { BreadcrumbListTransport } from '../../../../models/common/breadcrumb-model'
import { Offer } from '../../../../models/common/offers-model'
import { SuitablesModel } from '../../../../models/common/suitables-model'
import SearchResultListingPageTransport from '../../../../transports/search-result-listing-page-transport'
import { decodeFromBase64, encodeToBase64, getRelativeURL } from '../../../../utils/encoding-utils'
import { scrollToTop } from '../../../../utils/dom-utils'
import {
    filterClick,
    getFiltersConfiguration,
    getIsFilterLoading,
    getLastDataPathEncoded,
} from '../../../../redux/filters'

import { useRequestUserConfigContext } from '../../../../hooks/request'
import FilterSidebar from '../filter-sidebar'
import StickyPopularFilters from '../filter-sidebar/sticky-popular-filters'
import ListViewSection from '../list-view-section'
import MapPinSrlIcon from '../../../../assets/icons/map-pin-home.svg'
import MapIcon from '../../../../assets/icons/filter-icons/map.svg'
import BreadcrumbWrapper from '../../../common/breadcrumb-wrapper'
import {
    PageContent,
    ColumnsContainer,
    RightColumn,
    LeftColumn,
    ContentWrapper,
    MapWidgetContainer,
    MapButton,
    MapWidgetBackground,
    BlockingOverlay,
    LoaderDots,
    MobileMapButton,
    MapViewLabel,
    ListSectionWrapper,
} from './styles'
import useGridView from '../hooks'
import SearchRegionNodeTransport from '../../../../transports/search-region-node-transport'
import BulkInquiryTray from '../bulk-inquiry/bulk-inquiry-tray'
import { useSyncSelectionFilter } from '../../../../redux/bulk-inquiry'

interface Props {
    initialFilter: string
    staticData: SearchResultListingPageTransport['staticData']
    siteRelativeURL: string
    breadcrumbModel: BreadcrumbListTransport
    offerModel: Offer
    suitablesModel: SuitablesModel
    searchRegionNode: SearchRegionNodeTransport | null
}

const SearchResultListingContent: FC<Props> = ({
    initialFilter,
    staticData,
    siteRelativeURL,
    breadcrumbModel,
    offerModel,
    suitablesModel,
    searchRegionNode,
}) => {
    const { filterSection, mapView } = staticData
    const { isMobile } = useContext(ScreenContext)
    const dispatch = useAppDispatch()
    const user = useRequestUserConfigContext()

    const filtersConfiguration = useSelector(getFiltersConfiguration)
    const isFilterLoading = useSelector(getIsFilterLoading)
    const lastDataPathEncoded = useSelector(getLastDataPathEncoded)

    // Initialize currentFilter - it's already a base64 encoded relative URL now
    const [currentFilter, setCurrentFilter] = useState(() => decodeFromBase64(initialFilter))
    const { isGridView, toggleView, gridItemsPerRow } = useGridView()

    useSyncSelectionFilter(currentFilter)

    const sidebarRef = useRef<HTMLDivElement>(null)
    const [showStickyFilters, setShowStickyFilters] = useState(false)

    // Disable browser's automatic scroll restoration so our scrollToTop works on back/forward
    useEffect(() => {
        if ('scrollRestoration' in globalThis.history) {
            globalThis.history.scrollRestoration = 'manual'
        }
        return () => {
            if ('scrollRestoration' in globalThis.history) {
                globalThis.history.scrollRestoration = 'auto'
            }
        }
    }, [])

    // Sync URL and currentFilter when Redux state changes
    useEffect(() => {
        if (lastDataPathEncoded) {
            const decodedPath = decodeFromBase64(lastDataPathEncoded)
            if (decodedPath !== globalThis.location.pathname + decodeURIComponent(globalThis.location.search)) {
                globalThis.history.pushState({}, '', decodedPath)
                setCurrentFilter(decodedPath)
                scrollToTop('instant')
            }
        }
    }, [lastDataPathEncoded])

    // Handle browser back/forward navigation
    useEffect(() => {
        const handlePopState = () => {
            const url = new URL(globalThis.location.href)
            url.searchParams.delete('page')
            url.searchParams.delete('pageSize')
            url.searchParams.delete('offset')
            const cleanUrl = url.pathname + decodeURIComponent(url.search)

            globalThis.history.replaceState({}, '', cleanUrl)
            setCurrentFilter(cleanUrl)
            scrollToTop('instant')
            dispatch(filterClick(encodeToBase64(cleanUrl), true, user))
        }

        globalThis.addEventListener('popstate', handlePopState)
        return () => globalThis.removeEventListener('popstate', handlePopState)
    }, [dispatch, user])

    // Scroll detection for sticky popular filters
    useEffect(() => {
        const element = sidebarRef.current
        if (!element) {
            return
        }

        const observer = new IntersectionObserver(([entry]) => setShowStickyFilters(!entry.isIntersecting), {
            threshold: 0,
        })

        observer.observe(element)
        return () => observer.disconnect()
    }, [])

    const onMapClick = useCallback(
        (e: React.MouseEvent) => {
            e.preventDefault()
            if (filtersConfiguration?.mapLink) {
                globalThis.location.href = getRelativeURL(filtersConfiguration.mapLink, { encoded: false })
            }
        },
        [filtersConfiguration],
    )

    return (
        <ListSectionWrapper>
            {isFilterLoading && (
                <BlockingOverlay>
                    <LoaderDots>
                        <span />
                        <span />
                        <span />
                    </LoaderDots>
                </BlockingOverlay>
            )}

            <PageContent>
                {!isMobile && <BreadcrumbWrapper url={siteRelativeURL} containerPadding="0 0" />}

                <ContentWrapper>
                    <ColumnsContainer>
                        <LeftColumn>
                            <MapWidgetContainer onClick={onMapClick}>
                                <MapWidgetBackground />
                                <MapButton>
                                    <MapPinSrlIcon />
                                    <MapViewLabel>{mapView}</MapViewLabel>
                                </MapButton>
                            </MapWidgetContainer>
                            <>
                                <div ref={sidebarRef}>
                                    <FilterSidebar filterStaticData={filterSection} />
                                </div>

                                <StickyPopularFilters
                                    filterStaticData={staticData}
                                    isVisible={showStickyFilters}
                                    onMapClick={onMapClick}
                                />
                            </>
                        </LeftColumn>
                        <RightColumn>
                            <ListViewSection
                                filter={currentFilter}
                                isGridView={isGridView}
                                onToggleView={toggleView}
                                gridItemsPerRow={gridItemsPerRow}
                                breadcrumbModel={breadcrumbModel}
                                offerModel={offerModel}
                                suitablesModel={suitablesModel}
                                filterStaticData={filterSection}
                                searchRegionNode={searchRegionNode}
                            />
                        </RightColumn>
                    </ColumnsContainer>
                </ContentWrapper>
            </PageContent>
            <MobileMapButton onClick={onMapClick} aria-label={mapView}>
                <MapIcon />
                <MapViewLabel>{mapView}</MapViewLabel>
            </MobileMapButton>
            <BulkInquiryTray filter={currentFilter} searchRegionNode={searchRegionNode} />
        </ListSectionWrapper>
    )
}

export default memo(SearchResultListingContent)
